import React, { useMemo, useState } from "react";
import { Search, MapPin, Navigation, CheckCircle2 } from "lucide-react";
import { PageHeader, Reveal } from "@/components/public/ui";
import { Input } from "@/components/ui/input";
import { usePublic } from "@/hooks/usePublic";
import { useSite } from "@/context/SiteContext";
import { toMapEmbed } from "@/lib/maps";

const mapLink = (m) =>
  m.lat && m.lng
    ? `https://www.google.com/maps/search/?api=1&query=${m.lat},${m.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([m.address, m.city, m.state].filter(Boolean).join(", "))}`;

export default function MachineLocations() {
  const { data, loading } = usePublic("/machine-locations");
  const { settings } = useSite();
  const [q, setQ] = useState("");
  const list = data || [];

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((m) => [m.name, m.city, m.state, m.address, m.type].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [q, list]);

  const embed = filtered[0]
    ? `https://www.google.com/maps?q=${encodeURIComponent(filtered[0].lat && filtered[0].lng ? `${filtered[0].lat},${filtered[0].lng}` : [filtered[0].address, filtered[0].city, filtered[0].state].filter(Boolean).join(", "))}&output=embed`
    : toMapEmbed(settings.map_embed, settings.address);

  return (
    <div data-testid="machine-locations-page">
      <PageHeader eyebrow="Machine Locations" title="Find an Aimway station near you" subtitle="Our sanitization stations are installed across the country. Locate the nearest machine and sanitize on the go." />
      <section className="container-page py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="relative mb-6">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by city, state or venue..." className="h-12 border-slate-200 bg-white pl-11 focus-visible:ring-brand" data-testid="location-search" />
            </div>
            {loading ? (
              <div className="space-y-4">{[0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>
            ) : filtered.length ? (
              <div className="max-h-[520px] space-y-4 overflow-y-auto pr-1">
                {filtered.map((m, i) => (
                  <Reveal key={m.id} delay={i * 0.04}>
                    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card" data-testid={`machine-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 font-display font-700 text-brand-navy">{m.name} <CheckCircle2 size={15} className="text-emerald-500" /></div>
                          <div className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground"><MapPin size={15} className="mt-0.5 shrink-0 text-brand-orange" />{[m.address, m.city, m.state].filter(Boolean).join(", ")}</div>
                          {m.type && <span className="mt-2 inline-block rounded-full bg-brand-sky px-3 py-1 text-xs font-600 text-brand">{m.type}</span>}
                        </div>
                        <a href={mapLink(m)} target="_blank" rel="noreferrer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand text-white transition-colors hover:bg-brand-navy" aria-label="Directions"><Navigation size={16} /></a>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-muted-foreground">No locations found{q ? ` for "${q}"` : ""}. New installations coming soon.</div>
            )}
          </div>
          <div className="lg:col-span-3">
            <div className="h-full min-h-[400px] overflow-hidden rounded-2xl border border-slate-100 shadow-card">
              <iframe title="Machine map" src={embed} className="h-full min-h-[400px] w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="machine-map" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
